import { CONSISTENCY_FAILED_INSTANCES_URL } from 'utils/config';
import { shouldRenewTokenForDV } from 'utils/data-validation';
import { FailedInstance } from '../consistencyReportTypes';

type AuthState = {
  token: string | undefined;
  tenant: string | undefined;
  cluster: string | undefined;
  expiry: number | undefined;
};

type LoginFn = (opts: {
  tenant?: string;
  useOIDC?: boolean;
  forceRefresh?: boolean;
}) => Promise<{ token?: string }>;

const delay = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

const isRetriable = (err: unknown): boolean => {
  if (err instanceof RetriableHttpError) return true;
  // Network-level failures (TypeError from fetch)
  return err instanceof TypeError;
};

class RetriableHttpError extends Error {
  constructor(
    public readonly status: number,
    statusText: string,
    body: string
  ) {
    super(
      `Failed to fetch failed instances: ${status} ${statusText}${
        body ? ` - ${body}` : ''
      }`
    );
  }
}

class NonRetriableHttpError extends Error {
  constructor(status: number, statusText: string, body: string) {
    super(
      `Failed to fetch failed instances: ${status} ${statusText}${
        body ? ` - ${body}` : ''
      }`
    );
  }
}

const fetchWithRetry = async (
  url: string,
  headers: Record<string, string>,
  signal: AbortSignal | undefined,
  remainingTries: number
): Promise<Response> => {
  try {
    const res = await fetch(url, { headers, signal });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      if (res.status >= 500) {
        throw new RetriableHttpError(res.status, res.statusText, body);
      }
      throw new NonRetriableHttpError(res.status, res.statusText, body);
    }
    return res;
  } catch (err) {
    if (remainingTries > 0 && isRetriable(err)) {
      await delay(1000);
      return fetchWithRetry(url, headers, signal, remainingTries - 1);
    }
    throw err;
  }
};

export const fetchConsistencyFailedInstances = async (
  runStatusId: string,
  auth: AuthState,
  login: LoginFn,
  signal?: AbortSignal
): Promise<FailedInstance[]> => {
  let activeToken = auth.token ?? '';
  if (shouldRenewTokenForDV(auth.expiry)) {
    activeToken =
      (
        await login({
          tenant: auth.tenant,
          useOIDC: true,
          forceRefresh: true,
        })
      ).token ?? '';
  }

  const res = await fetchWithRetry(
    CONSISTENCY_FAILED_INSTANCES_URL(runStatusId),
    {
      Authorization: `Bearer ${activeToken}`,
      project: auth.tenant ?? '',
      cluster: auth.cluster ?? '',
    },
    signal,
    3
  );

  const body: unknown = await res.json();
  if (body === null) return [];
  if (!Array.isArray(body)) {
    throw new Error('Unexpected response format: expected an array');
  }
  return body;
};
